import { useMutation } from '@tanstack/react-query'
import type { TradeRehash } from '@greencandle/api-client'
import { api } from '../../api'
import { Failed } from '../primitives'

/**
 * FR-B11. Moves the imported fills onto the identity a CSV and a PDF of one statement agree on.
 *
 * <p>A fill used to be identified by its printed price and proceeds, and the PDF rounds proceeds
 * to the cent where the CSV download does not - so, over history imported from PDFs, the first
 * CSV would write every sub-cent fill a second time. This is the one-off that prevents it, and
 * the same thing {@code scripts/rehash-trades.py} does, from where the statements are loaded.
 *
 * <p>Same discipline as the balance backfill on the import page: a check first, which writes
 * nothing, and the button that writes appears only once the check has said what it would move.
 * It does not appear at all while {@code orphaned} is above zero - that is a statement deleted
 * after its commit or a fill stored twice, and the right response is a person looking, not a
 * button that carries on.
 */
export function RehashTradesPanel({ onClose }: { onClose: () => void }) {
  const run = useMutation({ mutationFn: (dryRun: boolean) => api.rehashTrades(dryRun) })
  const result: TradeRehash | undefined = run.data
  const checked = run.isSuccess && run.variables === true
  const applied = run.isSuccess && run.variables === false

  return (
    <section className="panel" aria-label="Rehash trades">
      <div className="panel-header">
        <h3>Rehash trades before a CSV import</h3>
        <button type="button" className="term-link" onClick={onClose}>
          close
        </button>
      </div>

      <div className="controls-box">
        <div className="control-group control-end">
          <button
            type="button"
            className="btn-primary"
            disabled={run.isPending}
            onClick={() => run.mutate(true)}
          >
            {run.isPending && run.variables === true ? 'Checking…' : 'Check'}
          </button>
          {checked && result && result.orphaned === 0 && result.upgraded > 0 && (
            <button
              type="button"
              className="btn-primary"
              disabled={run.isPending}
              onClick={() => run.mutate(false)}
            >
              Apply
            </button>
          )}
        </div>
        <div className="commit-note">
          Gives every imported trade the identity that is the same whether its statement was read
          as a PDF or a CSV, so a CSV covering days already imported adds nothing twice. Writes
          identities only - never a trade, a lot or an amount - and is safe to run again.
        </div>
      </div>

      {run.isError && <Failed error={run.error} />}

      {result && (
        <div className={`term-verdict ${result.orphaned > 0 ? 'bad' : 'ok'}`}>
          <span className={`badge ${result.orphaned > 0 ? 'badge-custom' : 'badge-re'}`}>
            {result.orphaned > 0 ? 'stop' : applied ? 'applied' : 'checked'}
          </span>
          <strong>{headline(result, applied)}</strong>
          <ul>
            <li>
              {applied ? 'Moved' : 'To move'}: {result.upgraded} · already current:{' '}
              {result.alreadyCurrent} · statements on file: {result.reportsFound}
            </li>
            {result.orphaned > 0 && (
              <li>
                {result.orphaned} imported trade{result.orphaned === 1 ? ' is' : 's are'} not
                accounted for by any stored statement. Find out why before importing any CSV.
              </li>
            )}
            {result.skipped.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/** Said so that whether anything was written is never left for the reader to infer. */
function headline(result: TradeRehash, applied: boolean): string {
  if (result.orphaned > 0) {
    return 'Not safe to apply: some imported trades have no statement behind them.'
  }
  if (applied) {
    return `${result.upgraded} trade${result.upgraded === 1 ? '' : 's'} moved. A CSV can be imported now.`
  }
  if (result.upgraded === 0) {
    return 'Nothing to move: every imported trade already has the current identity. A CSV can be imported now.'
  }
  return `${result.upgraded} trade${result.upgraded === 1 ? '' : 's'} would be moved. Nothing has been written yet.`
}
